import React, { Fragment, useEffect, useState } from 'react';

import InfoPartial from '@/features/products/detail/partials/info';
import GalleryPartial from '@/features/products/detail/partials/gallery';
import BreadCrumbPartial from '@/features/products/detail/partials/breadcrumb';
import { Product } from '@/features/products/shared';
import { useTranslation } from 'next-i18next';
import { useFetchRelatedProductsQuery } from '../list/api';
import toast from 'react-hot-toast';
import RelatedProduct from './partials/RelatedProduct';
import MetaTag from '@/components/ui/MetaTag';
import {
  ArrowLeftIcon,
  Button,
  ButtonVariant,
  DiamondFilledIcon,
  Icon,
  Link,
  LiveProduct,
  Loading,
  Modal,
  ModalBody,
  PentagonIcon,
  WalletIcon,
  Image,
} from '@/components';
import {
  AuctionStatus,
  DiscountStatus,
  PagePath,
  ProductStatus,
} from '@/layouts/shared';
import { checkTime, createHandle } from '@/libs/app';
import router from 'next/router';
import InfoMobile from './partials/InfoMobile';
import { ConversationType } from './const';
import {
  useCreateChatConversation,
  useFetchConversationByProductQuery,
  useRemindResellQuery,
} from './api';
import ReactPlayer from 'react-player';
import { useDisableBodyScroll } from '@/libs/hook';
import { humanDate } from '@/libs/dayjs';
import { CountDown } from './partials/info/countdown';
import { useFetchAuctionByProductId } from '@/features/livestream/detail/api';
import { AuctionModel } from '@/models/auction';
import { useMasterData } from '@/contexts/profile';
export type ProductDetailLayoutProps = {
  isAuth: boolean;
  product: Product;
  isMobile: boolean;
  handle: string;
  catCode?: string;
};

const ProductDetailLayout = ({
  product,
  isAuth,
  isMobile,
  handle,
  catCode,
}: ProductDetailLayoutProps) => {
  const { t } = useTranslation(['product', 'layout']);
  const masterData = useMasterData();
  const { theme } = masterData ?? {};
  const { icon_category_url } = theme ?? {};

  const {
    live_stream_product: liveStreamProducts,
    id: productId,
    is_auction: isAuction,
    is_partner: isPartner,
    product_discount,
    seo,
  } = product;
  let isDiscount = product_discount
    ? checkTime(product_discount?.started_at, product_discount?.ended_at)
    : false;
  if (product_discount && product_discount?.status === DiscountStatus.Ended)
    isDiscount = false;
  const [listProduct, setListProduct] = useState<Product[]>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [auction, setAuction] = useState<AuctionModel>();

  const { refetch, isLoading } = useFetchRelatedProductsQuery(
    {
      cat_code: product.cat_code,
      product_id: product.id,
    },
    {
      enabled: false,
      retry: true,
      retryDelay: 20,
      onError: ({ message }) => {
        toast.error(message);
      },
      onSuccess: ({ data }) => {
        let _data = data.filter((d) => d.product_code !== product.product_code);
        setListProduct(_data);
      },
    }
  );
  const { mutate: mutateRemindProduct } = useRemindResellQuery({
    onSuccess: ({ data }) => {
      if (data.remind) {
        toast.success('Đã chọn nhận thông báo khi sản phẩm được mở bán lại');
      } else {
        toast.success('Đã huỷ nhận thông báo khi sản phẩm được mở bán lại');
      }
    },
    onError: ({ message }) => {
      toast.error(message);
    },
  });
  const { refetch: fetchAuction } = useFetchAuctionByProductId(productId, {
    enabled: false,
    refetchOnWindowFocus: false,
    retry: false,
    onError: () => console.log('Failed to fetch auction'),
    onSuccess: ({ data }) => setAuction(data),
  });
  useEffect(() => {
    if (productId && isAuction) {
      fetchAuction();
    }
  }, [productId, isAuction, fetchAuction]);
  useEffect(() => {
    if (product.cat_code) {
      refetch();
    }
  }, [product.cat_code, refetch, product.id]);
  useDisableBodyScroll(showModal);
  const onDepositClick = () => {
    return router.push({
      pathname: PagePath.ProductDepositPage,
      query: { id: product.id },
    });
  };
  const { mutate, isLoading: isCreateChatConversationLoading } =
    useCreateChatConversation({
      onSuccess: ({ data }) => {
        const { id } = data.conversation || {};
        if (id) {
          return router.replace({
            query: { id },
            pathname: PagePath.ConsultChatPage,
          });
        }
      },
      onError: ({ message }) => {
        toast.error(message);
      },
    });

  const { refetch: fetchConversation, isLoading: isFetchConversationLoading } =
    useFetchConversationByProductQuery(product.id, {
      retry: 0,
      enabled: false,
      onSuccess: (data) => {
        const { id } = data.data || {};
        if (id) {
          return router.replace({
            query: { id },
            pathname: PagePath.ConsultChatPage,
          });
        }
      },
      onError: (error) => {
        const productImages = Array.isArray(product.images)
          ? product.images
          : [];
        let typeImage = '';
        if (productImages.length) {
          const { url } = productImages[0];
          typeImage = url;
        }
        return mutate({
          title: product.product_name,
          type_id: product.id,
          type_image: typeImage,
          type_code: product.product_code,
          type: ConversationType.Product,
        });
      },
    });
  const isConsultLoading =
    isFetchConversationLoading || isCreateChatConversationLoading;
  const onConsultClick = () => {
    if (!isAuth) {
      localStorage.setItem('productUrl', router.query?.id + '');
      return router.push(PagePath.LoginPage);
    } else {
      localStorage.removeItem('productUrl');
      return fetchConversation();
    }
  };
  const onRemindClick = (checked: boolean) => {
    mutateRemindProduct({
      product_id: product.id,
      remind: checked,
    });
  };
  const handleModal = () => setShowModal(true);
  const isDeposited =
    product.quantity <= 0 || product.status !== ProductStatus.Availability;

  if (!masterData) return <></>;

  return (
    <Fragment>
      <MetaTag
        title={product.product_name}
        description={product.description || product.product_name}
        image={product.images && product.images[0]?.url}
        type="product"
        price={product.price}
        productCode={product.product_code}
        updated={humanDate(product.created_at)}
        category={catCode || product.cat_code}
        productId={product.id}
        status={product.status}
        handle={'/products/' + handle}
        seo={seo}
      />

      <div className="container tw-mx-auto md:tw-border-t tw-border-primary-main md:tw-pt-6 md:tw-pb-14 tw-pb-20">
        <div className="tw-fixed tw-block md:tw-hidden tw-z-50 tw-top-6 tw-left-4">
          <div
            className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-10 tw-flex   tw-items-center tw-justify-center"
            onClick={() => router.back()}
          >
            <Icon
              className="tw-w-6 tw-h-6 tw-text-primary-main"
              icon={<ArrowLeftIcon />}
            />
          </div>
        </div>
        <div
          className={`tw-flex md:tw-pb-10 tw-flex-col md:tw-border-b tw-border-primary-main tw-border-opacity-50 tw-z-0 ${
            isPartner && '!tw-border-opacity-0'
          }`}
        >
          <BreadCrumbPartial
            categoryName={product.cat_name}
            categoryCode={product.cat_code}
            isPartner={product.is_partner}
            partnerId={product.partner?.id}
            partnerName={product.partner?.fullname}
            t={t}
            title={product.product_name}
          />
          <div className="tw-grid tw-grid-cols-1 md:tw-mt-10 md:tw-grid-cols-2 md:tw-gap-y-4 tw-gap-x-0 sm:tw-gap-x-4 sm:tw-gap-y-0 ">
            <div className="tw-flex tw-flex-col">
              {isAuction &&
                auction &&
                auction.status === AuctionStatus.Started && (
                  <div className="tw-py-3 tw-px-4 tw-flex tw-mb-3">
                    <CountDown
                      startTime={auction.started_at}
                      endTime={auction.ended_time_at}
                      t={t}
                    />
                    <div className="tw-ml-4 tw-mt-5 tw-text-secondary-70 tw-text-base tw-lowercase">
                      Thời gian đấu giá còn lại
                    </div>
                  </div>
                )}
              <div className="tw-relative tw-flex-1">
                {product.images ? (
                  <div className="tw--mx-2 md:tw-mx-0 tw-relative">
                    {product_discount && isDiscount && (
                      <>
                        <div className="tw-w-[43px] tw-h-[43px] tw-absolute tw-top-3 tw-right-3 tw-z-10">
                          <Image
                            src="/sale.png"
                            className="tw-w-full tw-h-full"
                            alt="sale"
                          ></Image>
                        </div>
                      </>
                    )}
                    {isDeposited && (
                      <div className="tw-absolute tw-z-10 tw-top-3 tw-left-0">
                        <PentagonIcon text={t('detail.label.soldOut')} />
                      </div>
                    )}
                    <GalleryPartial
                      handleModal={handleModal}
                      linkVideo={product.link_video}
                      images={product.images}
                      isMobile={isMobile}
                      t={t}
                    />{' '}
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
            </div>

            <div className="tw-hidden md:tw-block">
              <InfoPartial
                t={t}
                data={product}
                isAuth={isAuth}
                auction={auction}
                onDepositClick={onDepositClick}
                onConsultClick={onConsultClick}
                isDeposited={isDeposited}
                isConsultLoading={isConsultLoading}
                onRemindClick={onRemindClick}
              />
            </div>
            <div className="tw-block tw--mx-2 md:tw-hidden tw--mt-6 tw-z-1 custom-bg-info tw-bg-white">
              <InfoMobile
                t={t}
                data={product}
                isAuth={isAuth}
                onRemindClick={onRemindClick}
              />
            </div>
          </div>
        </div>
        {!!liveStreamProducts && !isPartner && (
          <div className="tw-mt-10 tw-hidden md:tw-block">
            <h3 className="tw-flex tw-items-center tw-text-primary-main">
              {icon_category_url ? (
                <Image
                  src={icon_category_url}
                  alt="star"
                  className="tw-h-5 tw-w-5 tw-mr-2"
                />
              ) : (
                <Icon
                  icon={<DiamondFilledIcon />}
                  className="tw-w-5 tw-h-5 tw-opacity-50 tw-mr-2"
                />
              )}
              <span className="tw-text-xl">{t('detail.label.view')}</span>
            </h3>
            <div className="tw-mt-6 tw-grid tw-grid-cols-2 tw-gap-4">
              {liveStreamProducts.map((live) => {
                let handle = `${createHandle(live.live_stream.name)}-${
                  live.live_stream_id
                }`;
                return (
                  live.status &&
                  live.start_datetime && (
                    <Link
                      url={{
                        pathname: PagePath.LiveVideoPage,
                        query: { id: handle, start: live.played },
                      }}
                      key={'live' + live.live_stream_id}
                    >
                      <div className="tw-bg-muted-main tw-py-3 tw-px-4 tw-rounded-5">
                        <LiveProduct data={live} />
                      </div>
                    </Link>
                  )
                );
              })}
            </div>
          </div>
        )}

        {isLoading ? (
          <Loading />
        ) : (
          listProduct && (
            <div className="tw-mt-8 md:tw-mt-10">
              <RelatedProduct
                cateCode={product.cat_code}
                products={listProduct}
                title={t('detail.relatedProduct')}
                t={t}
              />
            </div>
          )
        )}
        <div className="tw-grid tw-grid-cols-5 tw-gap-2 tw-items-center md:tw-hidden tw-fixed tw-left-0 tw-bottom-0 tw-z-20 tw-w-full tw-px-2 tw-bg-white tw-py-4">
          {product.quotation ? (
            <div className="tw-col-span-5 tw-w-full tw-flex tw-justify-center">
              <Button
                disabled={!product.quantity}
                onClick={onConsultClick}
                loading={isConsultLoading}
                className="tw-flex tw-items-center tw-justify-center !tw-w-44 !tw-rounded-5 sm:tw-w-1/2 tw-text-warning-90 tw-border-warning-90"
                variant={ButtonVariant.Outlined}
              >
                <div className="tw-text-lg tw-uppercase">
                  {t('detail.btn.contact')}
                </div>
              </Button>
            </div>
          ) : (
            <>
              <Button
                disabled={isDeposited}
                onClick={onConsultClick}
                loading={isConsultLoading}
                className={`tw-col-span-2 tw-flex tw-items-center tw-justify-center !tw-rounded-5 ${isPartner && 'tw-col-span-5'}`}
                variant={ButtonVariant.Outlined}
              >
                <div className="tw-flex tw-gap-x-4 tw-justify-center tw-font-light tw-text-lg tw-text-black">
                  {t('detail.btn.consult')}
                </div>
              </Button>

              {!isPartner && (
                <Button
                  disabled={!isAuth || isDeposited || isPartner}
                  onClick={onDepositClick}
                  className="w-flex tw-items-center tw-justify-center !tw-rounded-5 tw-col-span-3"
                >
                  <div className="tw-flex tw-gap-x-3 tw-justify-center tw-text-lg tw-capitalize">
                    <Icon icon={<WalletIcon />} />
                    <p>
                      {!isDeposited
                        ? t('detail.btn.deposit')
                        : t('detail.btn.soldOut')}
                    </p>
                  </div>
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {showModal && product.link_video && (
        <Modal
          className="!tw-max-w-4xl tw-bg-transparent tw-min-h-50-screen tw-h-auto tw-relative tw-flex tw-flex-col tw-gap-y-0 !tw-w-full !tw-p-2"
          clChildren="!tw-gap-y-0"
          onClose={() => {
            setShowModal(false);
          }}
        >
          <ModalBody className="!tw-h-full">
            <ReactPlayer
              // url="https://www.facebook.com/anthukimcuong/videos/735500608077198"
              url={product.link_video}
              width="100%"
              height={product.link_video.includes('facebook') ? '60%' : '60vh'}
              config={{
                facebook: {
                  attributes: {
                    height: isMobile ? '500px' : '550px',
                  },
                },
              }}
            ></ReactPlayer>
          </ModalBody>
          <div
            className="tw-absolute tw--bottom-8 tw-left-1/2 md:tw-hidden tw--translate-x-1/2 tw-py-1 tw-px-3 tw-text-secondary-70 tw-bg-white tw-rounded-5"
            onClick={() => setShowModal(false)}
          >
            Close
          </div>
        </Modal>
      )}
    </Fragment>
  );
};

export default ProductDetailLayout;
